import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { BackArrowIcon } from "../../assets/iconComponents/BackArrowIcon";
import {
  BackButton,
  BackText,
  NavigateBackToDasboard,
  UserReportContainer,
} from "./UserReport.styles";

import { LANDING_PAGE_URL } from "../../utils/urlConstants";
import GeneralInformation from "./GeneralInformation";
import BillingInfomation from "./BillingInformation";
import { GetSpecificRequisition } from "../Grid/Services/GridService";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { useLoginScreen } from "../../utils/hooks/useLoginScreen";
import TreatmentNotesUpload from "./TreatmentNotesUpload";
import { GenerateNotesModal } from "./GenerateNotesModal";
import { clearHash } from "../../utils/helpers";
import InternalNotes from "./InternalNotes";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { SendActionType } from "../shared/Services/SharedServices";
import { CfcDetailEligibilityBanner } from "../Cfc/CfcDetailEligibilityBanner";
import { CfcOrderModal } from "../Cfc/CfcOrderModal";
import { CfcPatientData } from "../Cfc/types";
import {
  buildCfcPatientDataFromDetailedRequisition,
  isEligibleForCfc,
} from "../Cfc/cfcGridUtils";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { IntlBabyPeek } from "./IntlBabyPeek";

const UserReport = (): React.ReactElement => {
  useLoginScreen();
  const history = useHistory();
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { sfID } = useParams<{ sfID: string }>();
  const { hash } = useLocation();
  const { enabled: providerCfcEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderCfc,
  );
  const isSalesRep = auth?.user_type === "sales_rep";
  const canCreateElectronicOrders =
    !isSalesRep && Boolean(auth?.is_electronic_ordering_enabled);
  const canAccessProviderCfc =
    providerCfcEnabled && (canCreateElectronicOrders || isSalesRep);
  const [recentDownload, setRecentDownland] = useState(false);
  const [reportData, setReportData] = useState<DetailedRequisitionSerializer>();
  const [cfcOrderModalData, setCfcOrderModalData] =
    useState<CfcPatientData | null>(null);
  const [cfcOrderSubmitted, setCfcOrderSubmitted] = useState(false);
  const [generateNotesModalOpen, setGenerateNotesModalOpen] =
    useState<DetailedRequisitionSerializer>();

  useEffect(() => {
    if (!auth) return;

    const params = new URLSearchParams(window.location.search);
    const emailQueryParam = params.get("e");
    const metaData = {
      from_type: emailQueryParam ?? "portal",
    };

    SendActionType(
      "ProviderViewReqDetails",
      setLoggedOut,
      auth.token,
      metaData,
    ).catch(() => undefined);
  }, []);

  useEffect(() => {
    const getFullReportInfo = async () => {
      if (auth) {
        const reportResponse = await GetSpecificRequisition(
          auth.token,
          sfID,
          setLoggedOut,
        );
        if (reportResponse?.ok) {
          const report =
            (await reportResponse.json()) as DetailedRequisitionSerializer;
          setReportData(report);
          setCfcOrderSubmitted(false);
        } else {
          history.push(LANDING_PAGE_URL);
        }
      }
    };
    if (!sfID) {
      history.push(LANDING_PAGE_URL);
    } else if (auth?.token) {
      getFullReportInfo();
    }
  }, [auth?.token]);

  useEffect(() => {
    if (reportData && hash.length) {
      const hashElement = document.querySelector(hash);
      hashElement?.scrollIntoView();
      clearHash();
    }
  }, [reportData]);

  return (
    <>
      {reportData ? (
        <UserReportContainer>
          <NavigateBackToDasboard>
            <BackButton onClick={() => history.push(LANDING_PAGE_URL)}>
              <BackArrowIcon />
            </BackButton>
            <BackText>{`${reportData.first_name} ${reportData.last_name}`}</BackText>
          </NavigateBackToDasboard>
          <TreatmentNotesUpload
            fullReport={reportData}
            setGenerateNotesModalOpen={setGenerateNotesModalOpen}
            recentDownload={recentDownload}
          />
          {canAccessProviderCfc &&
            isEligibleForCfc(reportData, {
              requireConditions: true,
              orderSubmitted: cfcOrderSubmitted,
            }) && (
              <CfcDetailEligibilityBanner
                cfcEligibleCollectByDate={
                  reportData.aneuploidy_report.cfc_eligible_collect_by_date
                }
                onOrderCfc={() =>
                  setCfcOrderModalData(
                    buildCfcPatientDataFromDetailedRequisition(reportData),
                  )
                }
              />
            )}
          <GeneralInformation
            fullReport={reportData}
            setFullReport={setReportData}
            canViewProviderCfcResults={canAccessProviderCfc}
          />
          {reportData.intl_baby_peek_status && (
            <IntlBabyPeek
              reportData={reportData}
              setReportData={setReportData}
            />
          )}
          <BillingInfomation
            fullReport={reportData}
            setFullReport={setReportData}
          />
          {generateNotesModalOpen && (
            <GenerateNotesModal
              setFullReport={setReportData}
              setGenerateNotesModalOpen={setGenerateNotesModalOpen}
              generateNotesModalOpen={generateNotesModalOpen}
              setRecentDownland={setRecentDownland}
            />
          )}
          <InternalNotes
            fullReport={reportData}
            setFullReport={setReportData}
          />
          {canAccessProviderCfc && cfcOrderModalData && (
            <CfcOrderModal
              open
              onClose={() => setCfcOrderModalData(null)}
              patientData={cfcOrderModalData}
              onOrderSubmitted={() => setCfcOrderSubmitted(true)}
              canSubmitOrder={canCreateElectronicOrders}
            />
          )}
        </UserReportContainer>
      ) : (
        <div
          style={{
            height: "100%",
            width: "100%",
          }}
        >
          <SpinnerLoading />
        </div>
      )}
    </>
  );
};

export default UserReport;
